class CartSellingPlanPicker extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.trigger = this.querySelector('.cart-selling-plan-picker__trigger');
    this.panel = this.querySelector('.cart-selling-plan-picker__panel');
    this.label = this.querySelector('.cart-selling-plan-picker__trigger-label');

    this.trigger?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.classList.contains('is-loading')) return;
      this.toggle();
    });

    this.querySelectorAll('.cart-selling-plan-picker__option').forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.classList.contains('is-loading')) return;
        if (option.classList.contains('is-selected')) {
          this.close();
          return;
        }

        this.dispatchEvent(
          new CustomEvent('selling-plan:change', {
            bubbles: true,
            detail: {
              sellingPlanId: option.dataset.sellingPlan || '',
              optionLabel: option.dataset.optionLabel || option.textContent.trim(),
            },
          })
        );
      });
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  toggle() {
    if (this.panel?.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  get cartItem() {
    return this.closest('.cart-item');
  }

  open() {
    document.querySelectorAll('cart-selling-plan-picker').forEach((picker) => {
      if (picker !== this) picker.close();
    });

    // Teleport panel to .drawer__footer's parent (drawer__inner) so it sits
    // outside cart-drawer-items overflow:auto but inside the drawer.
    // We use position:fixed with viewport coords — drawer__inner has a CSS
    // transform which normally traps fixed children, but we compensate by
    // computing the transform offset and subtracting it from coordinates.
    const drawerInner = this.closest('.drawer__inner');
    const teleportTarget = drawerInner || document.getElementById('CartDrawer') || document.body;
    this._teleportTarget = teleportTarget;

    this._panelPlaceholder = document.createComment('cart-selling-plan-picker-panel');
    this.panel.before(this._panelPlaceholder);
    teleportTarget.appendChild(this.panel);

    this.panel.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.cartItem?.classList.add('cart-item--picker-open');
    this._positionPanel();

    this._scrollListener = () => this.close();
    this._resizeListener = () => this._positionPanel();
    this._resizeObserver = new ResizeObserver(() => this._positionPanel());
    const scrollParent = this.closest('cart-drawer-items') || teleportTarget;
    scrollParent.addEventListener('scroll', this._scrollListener, { passive: true });
    window.addEventListener('resize', this._resizeListener, { passive: true });
    this._resizeObserver.observe(this.trigger);
    this._scrollParent = scrollParent;
  }

  _positionPanel() {
    if (!this.trigger || !this.panel || !this._teleportTarget) return;

    const triggerRect = this.trigger.getBoundingClientRect();
    const parentRect = this._teleportTarget.getBoundingClientRect();
    const gap = 6;
    const panelMaxHeight = 280;

    // Clamp top so panel never overlaps the cart header area
    const drawerHeader = document.querySelector('.drawer__header');
    const minTop = drawerHeader
      ? drawerHeader.getBoundingClientRect().bottom + gap
      : 0;

    const rawTop = triggerRect.bottom - parentRect.top + gap;
    const top = Math.max(rawTop, minTop - parentRect.top);

    // Available height from where panel starts to bottom of drawer
    const drawerBottom = parentRect.bottom;
    const panelTopAbsolute = parentRect.top + top;
    const spaceBelow = drawerBottom - panelTopAbsolute - gap;
    const availableHeight = Math.min(Math.max(spaceBelow, 100), panelMaxHeight);

    this.panel.style.position = 'absolute';
    this.panel.style.width = triggerRect.width + 'px';
    this.panel.style.left = (triggerRect.left - parentRect.left) + 'px';
    this.panel.style.right = 'auto';
    this.panel.style.top = top + 'px';
    this.panel.style.bottom = 'auto';
    this.panel.style.maxHeight = availableHeight + 'px';
    this.panel.style.zIndex = '10000';
    this.panel.style.overflowY = 'auto';
  }

  close() {
    if (!this.panel) return;

    // Return panel to its original position in the DOM
    if (this._panelPlaceholder && this._panelPlaceholder.parentNode) {
      this._panelPlaceholder.parentNode.insertBefore(this.panel, this._panelPlaceholder);
      this._panelPlaceholder.parentNode.removeChild(this._panelPlaceholder);
    } else if (!this.contains(this.panel)) {
      this.appendChild(this.panel);
    }
    this._panelPlaceholder = null;
    this._teleportTarget = null;

    this.panel.hidden = true;
    this.panel.style.cssText = '';
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.cartItem?.classList.remove('cart-item--picker-open');

    if (this._scrollParent && this._scrollListener) {
      this._scrollParent.removeEventListener('scroll', this._scrollListener);
    }
    if (this._resizeListener) {
      window.removeEventListener('resize', this._resizeListener);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    this._scrollListener = null;
    this._resizeListener = null;
    this._scrollParent = null;
  }

  onDocumentClick(event) {
    if (!this.contains(event.target) && !this.panel?.contains(event.target)) {
      this.close();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.trigger) this.trigger.disabled = isLoading;
  }
}

customElements.define('cart-selling-plan-picker', CartSellingPlanPicker);
